import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Box,
  Typography,
  Alert,
  CircularProgress,
} from '@mui/material';
import { attendanceWebApi } from './attendanceWebApi';

const EVENT_CHOICES = [
  { value: 'CLOCK_IN', label: 'Clock In' },
  { value: 'CLOCK_OUT', label: 'Clock Out' },
  { value: 'BREAK_START', label: 'Break Start' },
  { value: 'BREAK_END', label: 'Break End' },
];

function generateUuid() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

export default function WebRegularizationModal({ open, onClose, onSuccess, initialSessionId = null }) {
  const [eventType, setEventType] = useState('CLOCK_IN');
  const [datetimeStr, setDatetimeStr] = useState('');
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!datetimeStr) {
      setErrorMsg('Please select a date and time.');
      return;
    }
    if (!reason.trim()) {
      setErrorMsg('Please provide a reason for the regularization request.');
      return;
    }

    const requestedDate = new Date(datetimeStr);
    if (isNaN(requestedDate.getTime())) {
      setErrorMsg('Invalid date/time specified.');
      return;
    }
    if (requestedDate > new Date()) {
      setErrorMsg('Future timestamps cannot be regularized.');
      return;
    }

    setLoading(true);
    setErrorMsg('');

    try {
      const payload = {
        request_id: generateUuid(),
        event_type: eventType,
        requested_at: requestedDate.toISOString(), // Explicit UTC ISO string ending in 'Z'
        reason: reason.trim(),
      };
      if (initialSessionId) {
        payload.session_id = initialSessionId;
      }

      await attendanceWebApi.createRegularization(payload);
      setLoading(false);
      if (onSuccess) onSuccess();
      onClose();
    } catch (err) {
      setLoading(false);
      const data = err.response?.data;
      const code = data?.code || data?.detail || err.message;
      if (code === 'FUTURE_REQUEST_TIME') {
        setErrorMsg('The requested time cannot be in the future.');
      } else if (code === 'EXPLICIT_TIMEZONE_REQUIRED') {
        setErrorMsg('An explicit timestamp offset is required.');
      } else if (code === 'EMPLOYEE_INACTIVE') {
        setErrorMsg('Inactive or resigned employees cannot submit regularizations.');
      } else {
        setErrorMsg(`Failed to submit regularization: ${code || 'Unknown error'}`);
      }
    }
  };

  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit}>
        <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>Request Attendance Regularization</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Submit a missed punch or break adjustment for HR review. Approved requests record a documented review decision without modifying official payroll or biometric records.
          </Typography>

          {errorMsg && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErrorMsg('')}>
              {errorMsg}
            </Alert>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, mt: 1 }}>
            <TextField
              select
              label="Event Type"
              value={eventType}
              onChange={(e) => setEventType(e.target.value)}
              fullWidth
              size="small"
              required
            >
              {EVENT_CHOICES.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              type="datetime-local"
              label="Actual Event Date & Time"
              value={datetimeStr}
              onChange={(e) => setDatetimeStr(e.target.value)}
              fullWidth
              size="small"
              required
              InputLabelProps={{ shrink: true }}
              inputProps={{ max: new Date().toISOString().slice(0, 16) }}
              helperText="Authoritative timestamps are saved in UTC with your tenant timezone recorded."
            />

            <TextField
              label="Reason for Regularization"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              fullWidth
              multiline
              rows={3}
              required
              placeholder="e.g. Punch missed due to network outage while entering the office."
              helperText="A clear explanation is required for HR review."
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={onClose} disabled={loading} color="inherit">
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={18} color="inherit" /> : null}
            sx={{
              fontWeight: 700,
              background: 'linear-gradient(135deg,#3b82f6,#2f6bff)',
            }}
          >
            {loading ? 'Submitting…' : 'Submit Request'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
